import './bootstrap';

import { createApp } from 'vue/dist/vue.esm-bundler';
import * as Sentry from "@sentry/vue";
import store from '@/store';
import router from '@/router/router';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
    faPlay, faPause, faCog, faUpload, faPlus, faUser, faBell, faCogs, faSignOutAlt, faTimes, faMinus, faList,
    faDiceD20, faRedo, faTrash, faVolumeUp, faVolumeMute, faHandPaper, faRandom, faStop, faUserSecret, faMusic,
    faBug, faEdit, faExclamation, faCheckCircle, faExclamationCircle, faEyeSlash, faCopy, faUsers, faArrowLeft,
    faCircle, faBars, faArrowsAlt, faClock, faLock, faLockOpen, faMoon, faEnvelope, faFire, faSpinner,
    faInfoCircle, faCheck, faStepForward, faStepBackward, faDice, faBroadcastTower, faStar, faSync,
    faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import {
    faCloudDownload, faMusicSlash, faRepeat, faWandMagic, faUsersCrown, faUserCrown, faAlbum, faUserMusic, faStore,
    faBellOn, faAlbumCollection, faScrollOld, faSmilePlus, faExclamationSquare, faBookSpells, faSparkles
} from '@fortawesome/pro-solid-svg-icons';
import { faSignalAlt } from '@fortawesome/pro-light-svg-icons';
import { faSmileBeam } from '@fortawesome/pro-regular-svg-icons';
import { faInternetExplorer } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheckCircle as faCheckCircleRegular } from '@fortawesome/free-regular-svg-icons'

library.add(
    faPlay, faPause, faCog, faUpload, faPlus, faUser, faBell, faCogs, faSignOutAlt, faTimes, faMinus, faList,
    faDiceD20, faRedo, faTrash, faVolumeUp, faVolumeMute, faHandPaper, faRandom, faStop, faRepeat, faUserSecret,
    faRepeat, faMusic, faMusicSlash, faBug, faSmileBeam, faSignalAlt, faEdit, faWandMagic, faExclamation, faCloudDownload,
    faCheckCircle, faExclamationCircle, faEyeSlash, faUsersCrown, faCopy, faUserCrown, faAlbum, faUserMusic, faUsers, faArrowLeft,
    faStore, faBellOn, faCircle, faAlbumCollection, faBars, faArrowsAlt, faInfoCircle, faClock, faLock, faLockOpen, faMoon, faEnvelope,
    faScrollOld, faSmilePlus, faExclamationSquare, faBookSpells, faCheck, faStepForward, faStepBackward, faSparkles, faFire, faSpinner, faDice, faBroadcastTower,
    faSync, faStar, faCheckCircleRegular, faInternetExplorer, faQuestionCircle
);

// https://github.com/Maronato/vue-toastification/tree/next
import Toast, { POSITION, useToast } from "vue-toastification";
// Import the CSS or use your own!
import "vue-toastification/dist/index.css";
const options = {
    // You can set your default options here
    position: POSITION.TOP_CENTER,
    // timeout: false
};

import vueDebounce from 'vue-debounce';
import FloatingVue from 'floating-vue'

import AppTemplate from '@/views/template/Base.vue';

import HowlerPlugin from './howler';

const app = createApp(
    AppTemplate,
)
    .component('font-awesome-icon', FontAwesomeIcon)
    .use(store)
    .use(Toast, options)
    .use(FloatingVue)
    .use(HowlerPlugin)
    .use(vueDebounce);

const toastr = useToast();
// Add global method for showing toast
app.config.globalProperties.$accountLimitCheck = function(limitations, limit) {
    // console.log("Show global message", limitations, limit);
    if (!limitations || limitations.length === 0) {
        return false;
    }

    if (limit === 'SUBSCRIPTION' && limitations.includes(limit)) {
        toastr.error("Your account is limited due to a subscription issue. Please check your account settings for more information.");
        return true;
    }

    if (limit === 'STORAGE' && limitations.includes(limit)) {
        toastr.error("Your account is limited due to storage limit threshold. Please check your account settings for more information.");
        return true;
    }

    if (limit === 'SCENE' && limitations.includes(limit)) {
        toastr.error("Your account is limited due to scene limit threshold. Please check your account settings for more information.");
        return true;
    }

    if (limit === 'SOUNDBOARD' && limitations.includes(limit)) {
        toastr.error("Your account is limited due to soundboard limit threshold. Please check your account settings for more information.");
        return true;
    }

    return false;
};

if (import.meta.env.VITE_APP_ENV === 'production') {
    Sentry.init({
        app,
        dsn: "https://7e232fe98725ba8469089a9a3804e717@o446607.ingest.sentry.io/4505662338957312",
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                networkDetailAllowUrls: [window.location.origin, 'api.bardica.com'],
            }),
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
        ],
        tracePropagationTargets: ["localhost", /^\//, /bardica\.com/],
        // Performance Monitoring
        tracesSampleRate: 0.1, // Capture 100% of the transactions, reduce in production!
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        enableLogs: true, // Set to true to enable console.log, console.info, console.warn, console.error, and console.debug messages to be sent to Sentry as breadcrumbs.
    });
} else {
    console.log("Sentry not loaded in development mode.");
}

app.use(router);
app.mount('#app');
